import { useEffect, useState, useMemo } from 'react'
import { ordersApi, sellersApi } from '../../services/api'
import ExportBar, { ExportColumn } from '../../components/ExportBar'

type ItemStatus = 'pactado' | 'entregado' | 'cancelado'

interface OrderItem {
  id: number
  seller_product_id: number
  product_name: string
  presentation: string
  quantity: number
  unit_price: number
  status: ItemStatus
  cancellation_reason: string | null
  sale_id: number | null
}

interface Order {
  id: number
  seller_id: number
  seller_name: string
  customer_id: number | null
  customer_name: string | null
  customer_district: string | null
  agreed_date: string
  promised_date: string | null
  notes: string | null
  created_at: string
  items: OrderItem[]
}

const statusBadge: Record<ItemStatus, string> = {
  pactado: 'bg-amber-100 text-amber-800',
  entregado: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
}

const statusLabel: Record<ItemStatus, string> = {
  pactado: 'Pactado',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
}

function summarizeItemStatuses(items: OrderItem[]): string {
  const counts = items.reduce<Record<string, number>>((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1
    return acc
  }, {})
  return Object.entries(counts)
    .map(([status, count]) => `${statusLabel[status as ItemStatus]} (${count})`)
    .join(', ')
}

function orderTotal(order: Order): number {
  return order.items.reduce((sum, item) => sum + Number(item.unit_price) * item.quantity, 0)
}

const ORDERS_EXPORT_COLUMNS: ExportColumn<Order>[] = [
  { key: 'id', label: 'ID', format: (o) => `#${o.id}` },
  { key: 'agreed_date', label: 'Fecha', format: (o) => new Date(o.agreed_date).toLocaleDateString() },
  { key: 'seller_name', label: 'Vendedor', format: (o) => o.seller_name },
  { key: 'customer_name', label: 'Cliente', format: (o) => o.customer_name || 'Sin cliente' },
  { key: 'customer_district', label: 'Distrito', format: (o) => o.customer_district || '-' },
  { key: 'items_count', label: 'Cant. Items', format: (o) => String(o.items.length) },
  { key: 'status', label: 'Estado', format: (o) => summarizeItemStatuses(o.items) },
  { key: 'total', label: 'Total', format: (o) => `$${orderTotal(o).toFixed(2)}` },
]

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [sellers, setSellers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterSellerId, setFilterSellerId] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [filterDistrict, setFilterDistrict] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')

  useEffect(() => {
    ordersApi
      .list()
      .then((res) => setOrders(res.data.orders || []))
      .catch((err) => console.error('Error fetching orders:', err))
      .finally(() => setLoading(false))
    sellersApi
      .list()
      .then((res) => setSellers(res.data.sellers || []))
      .catch((err) => console.error('Error fetching sellers:', err))
  }, [])

  const districts = useMemo(() => {
    const set = new Set<string>()
    orders.forEach((o) => {
      if (o.customer_district) set.add(o.customer_district)
    })
    return Array.from(set).sort()
  }, [orders])

  const filtered = useMemo(() => {
    return orders
      .filter((o) => {
        if (filterSellerId && String(o.seller_id) !== filterSellerId) return false
        if (filterCustomer) {
          const q = filterCustomer.toLowerCase()
          if (!(o.customer_name || '').toLowerCase().includes(q)) return false
        }
        if (filterDistrict && o.customer_district !== filterDistrict) return false
        if (filterDateFrom) {
          const d = new Date(o.agreed_date)
          if (d < new Date(filterDateFrom)) return false
        }
        if (filterDateTo) {
          const d = new Date(o.agreed_date)
          if (d > new Date(filterDateTo + 'T23:59:59')) return false
        }
        if (filterStatus && !o.items.some((i) => i.status === filterStatus)) return false
        return true
      })
      .map((o) => ({
        ...o,
        items: filterStatus ? o.items.filter((i) => i.status === filterStatus) : o.items,
      }))
  }, [orders, filterSellerId, filterCustomer, filterDistrict, filterStatus, filterDateFrom, filterDateTo])

  const clearFilters = () => {
    setFilterSellerId('')
    setFilterCustomer('')
    setFilterDistrict('')
    setFilterStatus('')
    setFilterDateFrom('')
    setFilterDateTo('')
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Pedidos</h1>

      {/* Filters */}
      <div className="bg-cream-50 shadow rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-espresso-700">Filtros</h3>
          <button onClick={clearFilters} className="text-xs text-terracotta-700 hover:text-terracotta-800">
            Limpiar todo
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Vendedor</label>
            <select value={filterSellerId} onChange={(e) => setFilterSellerId(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name}{s.status !== 'active' ? ' (inactivo)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Estado</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              <option value="pactado">Pactado</option>
              <option value="entregado">Entregado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Desde</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Hasta</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Cliente</label>
            <input type="text" value={filterCustomer} placeholder="Buscar..."
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-espresso-500 mb-1">Distrito</label>
            <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)}
              className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
              <option value="">Todos</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="mb-3 text-sm text-espresso-600">
        {filtered.length} pedido{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay pedidos que coincidan con los filtros.</p>
        </div>
      ) : (
        <>
        <ExportBar data={filtered} columns={ORDERS_EXPORT_COLUMNS} filename="pedidos" title="Pedidos" />
        <div className="space-y-4">
          {filtered.map((order) => (
            <div key={order.id} className="bg-cream-50 shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-cream-50 border-b border-espresso-100 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-espresso-800">Pedido #{order.id}</span>
                  <span className="text-espresso-500 text-sm ml-2">
                    {order.seller_name} · {order.customer_name || 'Sin cliente'}
                    {order.customer_district && ` · ${order.customer_district}`}
                  </span>
                </div>
                <div className="text-xs text-espresso-500">
                  Pactado: {new Date(order.agreed_date).toLocaleDateString()}
                  {order.promised_date && (
                    <span className="ml-3">Entrega prometida: {new Date(order.promised_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              {order.notes && (
                <div className="px-4 py-2 text-sm text-espresso-600 border-b border-espresso-100">{order.notes}</div>
              )}
              <div className="divide-y divide-espresso-100">
                {order.items.map((item) => (
                  <div key={item.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm">
                      <span className="font-medium text-espresso-800">
                        {item.product_name} × {item.quantity} ({item.presentation})
                      </span>
                      <span className="text-espresso-500 ml-2">
                        ${Number(item.unit_price).toFixed(2)} c/u · Total: $
                        {(Number(item.unit_price) * item.quantity).toFixed(2)}
                      </span>
                      {item.status === 'cancelado' && item.cancellation_reason && (
                        <div className="text-xs text-red-600 mt-1">Motivo: {item.cancellation_reason}</div>
                      )}
                      {item.status === 'entregado' && item.sale_id && (
                        <div className="text-xs text-green-600 mt-1">Venta #{item.sale_id}</div>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge[item.status]}`}>
                      {statusLabel[item.status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  )
}
